import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  useMediaQuery,
  Stack,
  Pagination,
  Chip,
  Menu,
  AppBar,
  Toolbar,
  Slide,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Search, X, Building, Trash2, Calendar, ShoppingCart, Filter } from 'lucide-react';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import api from '../services/api';
import { useRefresh } from '../contexts/RefreshContext';

const FullScreenDialogTransition = React.forwardRef(function FullScreenDialogTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DeletedSalesPage = () => {
  // States
  const [deletedSales, setDeletedSales] = useState([]);
  const [selectedSaleItems, setSelectedSaleItems] = useState([]);
  const [hotels, setHotels] = useState([{ id: '', name: 'All Hotels' }]);
  const [loading, setLoading] = useState(false);
  const [loadingDialog, setLoadingDialog] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [search, setSearch] = useState('');
  const searchTimeoutRef = useRef(null);

  const [filters, setFilters] = useState({
    hotel_id: '',
    start_date: null,
    end_date: null,
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { refreshKey } = useRefresh();

  // ✅ filters UI (match SalesPage top)
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Fetch hotels
  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const response = await api.get('/admin/hotels');
        setHotels([{ id: '', name: 'All Hotels' }, ...(response.data || [])]);
      } catch (error) {
        console.error('Error loading hotels:', error);
        showSnackbar('Error loading hotels', 'error');
      }
    };
    fetchHotels();
  }, []);

  // Function to fetch deleted sales with pagination and filters
  const fetchDeletedSales = useCallback(async (currentPage, currentSearch, currentFilters, showLoading = true) => {
    if (showLoading) setLoading(true);

    try {
      const currentRowsPerPage = 50;
      setRowsPerPage(currentRowsPerPage);

      const params = {
        page: currentPage + 1,
        per_page: currentRowsPerPage,
        search: (currentSearch || '').trim(),
      };

      if (currentFilters.hotel_id) params.hotel_id = currentFilters.hotel_id;
      if (currentFilters.start_date) params.start_date = format(currentFilters.start_date, 'yyyy-MM-dd');
      if (currentFilters.end_date) params.end_date = format(currentFilters.end_date, 'yyyy-MM-dd');

      const response = await api.get('/admin/deleted-sales', { params });

      setDeletedSales(response.data?.deleted_sales || []);
      const paginationData = response.data?.pagination;
      const fetchedTotalRows = paginationData?.total_rows || response.data?.count || 0;

      setTotalRows(fetchedTotalRows);
      setTotalPages(paginationData?.total_pages || Math.ceil(fetchedTotalRows / currentRowsPerPage));
    } catch (error) {
      console.error('Error fetching deleted sales:', error);
      showSnackbar('Error loading deleted sales', 'error');
      setDeletedSales([]);
      setTotalRows(0);
      setTotalPages(0);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  // load when page changes
  useEffect(() => {
    fetchDeletedSales(page, search, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, fetchDeletedSales]);

  // search debounce
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setPage(0);
      fetchDeletedSales(0, search, filters);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // filters change
  useEffect(() => {
    setPage(0);
    fetchDeletedSales(0, search, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // refresh context
  useEffect(() => {
    fetchDeletedSales(page, search, filters, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Dialog handlers
  const handleDialogClose = () => {
    setDialogOpen(false);
    setTimeout(() => {
      setSelectedSale(null);
      setSelectedSaleItems([]);
    }, 150);
  };

  const handleOpenDetailsDialog = useCallback(async (saleData) => {
    if (!saleData) {
      showSnackbar('Invalid sale data.', 'error');
      return;
    }
    setLoadingDialog(true);
    setSelectedSale(saleData);
    setSelectedSaleItems(saleData.items || []);
    setDialogOpen(true);
    setLoadingDialog(false);
  }, []);

  // utils
  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '$0.00';
    return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const formatLocalId = (localId) => {
    if (!localId) return 'N/A';
    if (String(localId).startsWith('local_')) return String(localId).slice(-5);
    return localId;
  };

  // filters
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  const clearFilters = () => {
    setFilters({ hotel_id: '', start_date: null, end_date: null });
    setSearch('');
    setFilterMenuAnchor(null);
    setFilterDialogOpen(false);
  };

  // Pagination
  const handleChangePage = (_event, newPage) => setPage(newPage - 1);

  // ✅ top UI helpers
  const hasActiveFilters = Boolean(
    (search && search.trim()) || filters.hotel_id || filters.start_date || filters.end_date
  );

  const openFilters = (e) => {
    if (isMobile) setFilterDialogOpen(true);
    else setFilterMenuAnchor(e.currentTarget);
  };

  const closeFilters = () => {
    setFilterMenuAnchor(null);
    setFilterDialogOpen(false);
  };

  const FiltersContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <FormControl size="small" fullWidth>
        <InputLabel>Hotel</InputLabel>
        <Select
          value={filters.hotel_id}
          label="Hotel"
          onChange={(e) => handleFilterChange('hotel_id', e.target.value)}
          startAdornment={
            <InputAdornment position="start">
              <Building size={18} />
            </InputAdornment>
          }
        >
          {hotels.map((hotel) => (
            <MenuItem key={hotel.id || 'all'} value={hotel.id}>
              {hotel.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
          <DatePicker
            label="Start Date"
            value={filters.start_date}
            onChange={(newValue) => handleFilterChange('start_date', newValue)}
            maxDate={new Date()}
            format="MM/dd/yyyy"
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Calendar size={18} />
                    </InputAdornment>
                  ),
                },
              },
            }}
          />

          <DatePicker
            label="End Date"
            value={filters.end_date}
            onChange={(newValue) => handleFilterChange('end_date', newValue)}
            maxDate={new Date()}
            minDate={filters.start_date || undefined}
            format="MM/dd/yyyy"
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Calendar size={18} />
                    </InputAdornment>
                  ),
                },
              },
            }}
          />
        </Box>
      </LocalizationProvider>
    </Box>
  );

  const SearchBar = (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        p: 0,
        overflow: 'hidden',
        border: '2px solid transparent',
        backgroundImage: `
          linear-gradient(#fff, #fff),
          linear-gradient(180deg, #ec4899, #a855f7)
        `,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
      }}
    >
      <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.92)' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch" justifyContent="space-between">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="stretch" flex={1}>
            <TextField
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search employee name, local ID, hotel..."
              size="small"
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': { borderRadius: 2, height: 40 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearch('')} size="small" edge="end" sx={{ mr: -0.5 }}>
                      <X size={16} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: 'space-between', md: 'flex-end' }}
            sx={{ flexWrap: 'wrap', gap: 1 }}
          >
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                alignItems: 'center',
                gridTemplateColumns: { xs: '1fr 1fr', md: 'auto auto' },
                justifyContent: { xs: 'stretch', md: 'flex-end' },
              }}
            >
              <Button
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                onClick={openFilters}
                startIcon={<Filter size={16} />}
                size="small"
                sx={{
                  height: 40,
                  fontWeight: 900,
                  borderRadius: 2,
                  width: { xs: '100%', md: 'auto' },
                  minWidth: { md: 110 },
                  whiteSpace: 'nowrap',
                }}
              >
                Filters
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="text"
                  color="inherit"
                  onClick={clearFilters}
                  startIcon={<X size={16} />}
                  size="small"
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    height: 40,
                    fontWeight: 900,
                    borderRadius: 2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Stack>
        </Stack>

        {/* DESKTOP: Menu */}
        <Menu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={closeFilters}
          PaperProps={{ sx: { p: 2, borderRadius: 2, width: 420, maxWidth: 460 } }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography fontWeight={900}>Advanced Filters</Typography>
            <IconButton onClick={closeFilters} size="small">
              <X size={16} />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          {FiltersContent}

          <Divider sx={{ my: 1.5 }} />

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="text" color="inherit" onClick={clearFilters} startIcon={<X size={16} />} sx={{ fontWeight: 800 }}>
              Clear
            </Button>
            <Button variant="contained" onClick={closeFilters} sx={{ fontWeight: 900 }}>
              Done
            </Button>
          </Stack>
        </Menu>

        {/* MOBILE: Fullscreen Dialog */}
        <Dialog fullScreen open={filterDialogOpen} onClose={closeFilters} TransitionComponent={FullScreenDialogTransition}>
          <AppBar sx={{ position: 'relative' }}>
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Filter size={18} />
                <Typography fontWeight={900}>Filters</Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                {hasActiveFilters && (
                  <Button color="inherit" onClick={clearFilters} sx={{ fontWeight: 900 }}>
                    Clear
                  </Button>
                )}
                <Button color="inherit" onClick={closeFilters} sx={{ fontWeight: 900 }}>
                  Close
                </Button>
              </Stack>
            </Toolbar>
          </AppBar>

          <Box sx={{ p: 2 }}>
            <Typography fontWeight={900} sx={{ mb: 1 }}>
              Advanced Filters
            </Typography>

            {FiltersContent}

            <Box sx={{ mt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={closeFilters}
                sx={{ height: 44, borderRadius: 2, fontWeight: 900 }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Dialog>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {SearchBar}

        {/* Table */}
        <Box
          sx={{
            mt: 2,
            overflowX: 'auto',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            width: '100%',
          }}
        >
          <Paper sx={{ backgroundColor: '#fff', borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { backgroundColor: '#f0f0f0', fontWeight: 'bold', whiteSpace: 'nowrap' } }}>
                    <TableCell>Local ID</TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Hotel</TableCell>
                    <TableCell>Total Amount</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell>Original Date</TableCell>
                    <TableCell>Deleted Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && deletedSales.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : !loading && deletedSales.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No deleted sales found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    deletedSales.map((sale) => (
                      <TableRow
                        hover
                        key={sale.id}
                        onClick={() => handleOpenDetailsDialog(sale)}
                        sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)', cursor: 'pointer' } }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Chip label={formatLocalId(sale.local_sale_id)} size="small" color="error" variant="outlined" />
                        </TableCell>
                        <TableCell>{sale.employee_name || 'Unknown'}</TableCell>
                        <TableCell>{sale.hotel_name || `Hotel ${sale.hotel_id}`}</TableCell>
                        <TableCell>
                          <Typography fontWeight="medium" color="error.main">
                            {formatCurrency(sale.total_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={`${sale.items?.length || 0} items`} size="small" color="info" variant="outlined" />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {sale.original_created_at ? format(parseISO(sale.original_created_at), 'MM/dd/yy HH:mm') : '-'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {sale.deleted_at ? format(parseISO(sale.deleted_at), 'MM/dd/yy HH:mm') : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        {/* Pagination */}
        {!loading && totalRows > 0 && (
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" mt={2} px={1} mx="auto">
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {`Records ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalRows)} of ${totalRows}`}
            </Typography>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={handleChangePage}
              color="primary"
              shape="rounded"
              size={isMobile ? 'small' : 'medium'}
            />
          </Stack>
        )}

        {/* Details Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
        >
          <DialogTitle
            sx={{
              fontWeight: 'bold',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 1,
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Trash2 size={24} color="#f44336" />
              <span>Deleted Sale Details - ID: {formatLocalId(selectedSale?.local_sale_id)}</span>
            </Box>
            {loadingDialog && <CircularProgress size={24} />}
          </DialogTitle>

          <DialogContent dividers>
            {loadingDialog ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : selectedSale ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Sale Info */}
                <Box sx={{ p: 2, backgroundColor: '#fafafa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <Typography variant="h6" fontWeight="bold" mb={2} color="error.main">
                    Sale Information
                  </Typography>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Employee
                      </Typography>
                      <Typography fontWeight="medium">{selectedSale.employee_name || 'Unknown'}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Hotel
                      </Typography>
                      <Typography fontWeight="medium">{selectedSale.hotel_name || `Hotel ${selectedSale.hotel_id}`}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Total Amount
                      </Typography>
                      <Typography fontWeight="bold" color="error.main" variant="h6">
                        {formatCurrency(selectedSale.total_amount)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Local ID
                      </Typography>
                      <Chip label={formatLocalId(selectedSale.local_sale_id)} size="small" color="error" variant="outlined" />
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Original Created
                      </Typography>
                      <Typography fontWeight="medium">
                        {selectedSale.original_created_at
                          ? format(parseISO(selectedSale.original_created_at), 'MM/dd/yyyy HH:mm:ss')
                          : 'N/A'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Deleted At
                      </Typography>
                      <Typography fontWeight="medium">
                        {selectedSale.deleted_at ? format(parseISO(selectedSale.deleted_at), 'MM/dd/yyyy HH:mm:ss') : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider />

                {/* Items List */}
                <Box>
                  <Typography variant="h6" fontWeight="bold" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShoppingCart size={20} />
                    Items ({selectedSaleItems.length})
                  </Typography>

                  {selectedSaleItems.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px dashed #ddd' }}>
                      <Typography color="text.secondary">No items found</Typography>
                    </Box>
                  ) : (
                    <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: '#f0f0f0' } }}>
                            <TableCell>Item Name</TableCell>
                            <TableCell align="center">Quantity</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedSaleItems.map((item, index) => {
                            const quantity = parseFloat(item.quantity) || 0;
                            const price = parseFloat(item.price) || 0;
                            const total = quantity * price;

                            return (
                              <TableRow key={index} sx={{ bgcolor: 'white' }}>
                                <TableCell>
                                  <Typography fontWeight="medium">{item.name || 'Unnamed Item'}</Typography>
                                  {item.abbreviation && item.abbreviation !== item.name && (
                                    <Typography variant="caption" color="text.secondary">
                                      ({item.abbreviation})
                                    </Typography>
                                  )}
                                </TableCell>

                                <TableCell align="center">
                                  <Chip
                                    label={quantity % 1 === 0 ? quantity.toString() : quantity.toFixed(2)}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>

                                <TableCell align="right">{formatCurrency(price)}</TableCell>

                                <TableCell align="right">
                                  <Typography fontWeight="medium">{formatCurrency(total)}</Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography>No sale data available</Typography>
            )}
          </DialogContent>

          {/* Dialog Actions */}
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={handleDialogClose}
              startIcon={<X size={18} />}
              sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                color: '#333',
                border: '1px solid #ddd',
                '&:hover': { backgroundColor: '#e0e0e0' },
                minWidth: 120,
              }}
            >
              Close
            </Button>
          </Box>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default DeletedSalesPage;
